import * as React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { useState, useEffect } from "react";
import { useAuthDetails } from "../Common/cookiesHelper";
import { useDispatch, useSelector } from "react-redux";
import { fetchExportReportByMail } from "../Redux/features/Reports/NewCustomersAddedReport/NewCustomersAddedReportSlice";
import { ToastifyAlert } from "../CommonComponents/ToastifyAlert";
import CircularProgress from "@mui/material/CircularProgress";
import { X, Plus, Trash2 } from "lucide-react";
import "../Styles/ExportReportModal.scss";

/* Semantic tokens (tokens.css). Classic Light fallbacks are the QA hex;
   Warm Light and Dark remap the same names — never pin raw hex here. */
const C = {
  text: "var(--qv-text, #0f1115)",
  textMuted: "var(--qv-text-muted, #7a818f)",
  textSecondary: "var(--qv-tx-4b5563, #4b5563)",
  textDanger: "var(--qv-tx-c92a2a, #c92a2a)",
  cardBg: "var(--qv-surface, #ffffff)",
  inputBg: "var(--qv-bg-f7f8fa, #f7f8fa)",
  border: "var(--qv-border-input, #e3e6eb)",
  borderSoft: "var(--qv-border, #eef0f3)",
  btnDisabled: "var(--qv-bg-cdd2d9, #cdd2d9)",
  onSubmit: "var(--qv-surface, #ffffff)",
  backdrop: "rgba(15, 17, 21, 0.55)",
  shadow: "0 24px 60px -10px rgba(15, 17, 21, 0.4)",
  font: "CircularSTDBook, sans-serif",
};

const iconBtnStyle = {
  width: 36,
  height: 36,
  borderRadius: 8,
  border: `1px solid ${C.border}`,
  background: C.cardBg,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  color: C.text,
};

const inputStyle = {
  width: "100%",
  height: 40,
  padding: "0 12px",
  borderRadius: 8,
  border: `1px solid ${C.border}`,
  fontSize: 14,
  fontFamily: C.font,
  outline: "none",
  boxSizing: "border-box",
  background: C.cardBg,
  color: C.text,
};

export default function ExportReportModal({
  openModal,
  handleCloseModal,
  storeEmail,
  selectedDateRange,
  endPointObj,
  extraPayload = {},
  onSubmit,
  // The API only queues the report — callers can word the confirmation to say so
  successMessage = "Export Report Successfully",
}) {
  const { LoginGetDashBoardRecordJson, userTypeData } = useAuthDetails();
  const merchant_id = LoginGetDashBoardRecordJson?.data?.merchant_id;
  const { exportReportPayload } = useSelector((state) => state.SalesReportList);
  const dispatch = useDispatch();
  const [emailField, setEmailField] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!openModal) {
      setEmailField([]);
      setLoading(false);
    }
  }, [openModal]);

  const sendMailRequestForExportReport = async (payload) => {
    setLoading(true);
    try {
      const res = await dispatch(fetchExportReportByMail(payload)).unwrap();
      if (res?.msg === "report successfully added to the queue") {
        handleCloseModal();
        ToastifyAlert(successMessage, "success");
      } else {
        ToastifyAlert(res?.msg || "Failed to export report", "error");
      }
    } catch (error) {
      ToastifyAlert(
        error?.message || error?.data?.msg || "Failed to export report",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Used when the caller owns the export API (e.g. Inventory Report posts to
  // InventoryReportPk/inventory_report_export with its own column/filter payload).
  const runCallerSubmit = async (recipients) => {
    setLoading(true);
    try {
      await onSubmit(recipients);
    } catch (error) {
      ToastifyAlert(
        error?.message || error?.data?.message || "Failed to export report",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();
    if (loading) return;

    const additionalEmails = emailField
      ?.map((i) => i.value?.trim())
      .filter(Boolean);

    if (typeof onSubmit === "function") {
      const recipients = [storeEmail, ...(additionalEmails || [])]
        .map((i) => i?.trim())
        .filter(Boolean)
        .join(",");
      runCallerSubmit(recipients);
      return;
    }

    const payload = {
      ...exportReportPayload,
      merchant_id: exportReportPayload?.merchant_id || merchant_id,
      start_date:
        exportReportPayload?.start_date || selectedDateRange?.start_date || "",
      end_date:
        exportReportPayload?.end_date || selectedDateRange?.end_date || "",
      ...userTypeData,
      ...extraPayload,
      report_name: endPointObj,
      merchant_email: storeEmail,
      additional_email: additionalEmails?.length
        ? additionalEmails.join(",")
        : "",
    };

    sendMailRequestForExportReport(payload);
  };

  const handleAddNewEmailField = () => {
    setEmailField((prev) => {
      const newId = prev.length > 0 ? prev[prev.length - 1].id + 1 : 1;
      return [
        ...prev,
        {
          id: newId,
          name: "email",
          placeholder: "Enter Email",
          type: "email",
          value: "",
        },
      ];
    });
  };

  const handleDeleteNewEmailField = (id) => {
    setEmailField((prev) => prev.filter((i) => i?.id !== id));
  };

  const handleChange = (e, id) => {
    const { value } = e.target;
    setEmailField((prev) =>
      prev.map((i) => (i?.id === id ? { ...i, value } : i))
    );
  };

  return (
    <Modal
      open={openModal}
      onClose={loading ? undefined : handleCloseModal}
      aria-labelledby="export-report-modal-title"
      aria-describedby="export-report-modal-description"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: C.backdrop,
            backdropFilter: "blur(2px)",
          },
        },
      }}
    >
      <Box
        className="qv-order-modal-panel qv-export-report-modal"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 520,
          maxWidth: "calc(100% - 32px)",
          maxHeight: "90vh",
          bgcolor: C.cardBg,
          color: C.text,
          borderRadius: "16px",
          boxShadow: C.shadow,
          outline: 0,
          overflow: "hidden",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          fontFamily: C.font,
        }}
      >
        <div
          style={{
            padding: "20px 24px 16px",
            borderBottom: `1px solid ${C.borderSoft}`,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div>
            <div
              id="export-report-modal-title"
              style={{
                fontSize: 17,
                fontWeight: 700,
                color: C.text,
                fontFamily: C.font,
              }}
            >
              Export report
            </div>
            <div
              id="export-report-modal-description"
              style={{
                fontSize: 13,
                color: C.textMuted,
                marginTop: 4,
                lineHeight: 1.45,
                fontFamily: C.font,
              }}
            >
              This is the store&apos;s default email address, and the report will
              be sent to this email{" "}
              <b style={{ color: C.text }}>({storeEmail})</b>. If you wish to
              send it to additional email addresses, click Add Email. Otherwise,
              click Submit.
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              if (!loading) handleCloseModal();
            }}
            disabled={loading}
            aria-label="Close"
            className="qv-export-report-modal__icon-btn"
            style={{
              ...iconBtnStyle,
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.6 : 1,
            }}
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmitForm}>
          <div style={{ padding: "20px 24px" }}>
            <label
              style={{
                display: "block",
                fontSize: 11,
                color: C.textMuted,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                fontWeight: 600,
                marginBottom: 8,
                fontFamily: C.font,
              }}
            >
              Email
            </label>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: emailField?.length ? 12 : 0,
              }}
            >
              <input
                type="email"
                value={storeEmail || ""}
                readOnly
                className="qv-export-report-modal__input"
                style={{
                  ...inputStyle,
                  background: C.inputBg,
                  color: C.textSecondary,
                }}
              />
              {emailField?.length < 2 ? (
                <button
                  type="button"
                  onClick={handleAddNewEmailField}
                  aria-label="Add email"
                  className="qv-export-report-modal__icon-btn"
                  style={iconBtnStyle}
                >
                  <Plus size={16} />
                </button>
              ) : null}
            </div>

            {emailField?.map((item) => (
              <div
                key={item.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 12,
                }}
              >
                <input
                  type={item.type}
                  name={item.name}
                  placeholder={item.placeholder}
                  value={item.value}
                  onChange={(e) => handleChange(e, item.id)}
                  className="qv-export-report-modal__input"
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => handleDeleteNewEmailField(item.id)}
                  aria-label="Remove email"
                  className="qv-export-report-modal__icon-btn qv-export-report-modal__icon-btn--danger"
                  style={{ ...iconBtnStyle, color: C.textDanger }}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>

          <div
            style={{
              padding: "14px 20px",
              borderTop: `1px solid ${C.borderSoft}`,
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              onClick={handleCloseModal}
              disabled={loading}
              className="qv-export-report-modal__cancel"
              style={{
                height: 38,
                padding: "0 16px",
                borderRadius: 8,
                background: C.cardBg,
                border: `1px solid ${C.border}`,
                fontSize: 13.5,
                fontWeight: 600,
                color: C.text,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
                fontFamily: C.font,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="qv-export-report-modal__submit"
              style={{
                height: 38,
                padding: "0 18px",
                borderRadius: 8,
                background: loading ? C.btnDisabled : C.text,
                border: "none",
                color: C.onSubmit,
                fontSize: 13.5,
                fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
                fontFamily: C.font,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                minWidth: 120,
              }}
            >
              {loading ? (
                <CircularProgress size={16} sx={{ color: "inherit" }} />
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </form>
      </Box>
    </Modal>
  );
}
